import { FC, ChangeEvent, Dispatch, SetStateAction, useState, useEffect } from 'react'

import DatePicker from 'components/ui/transaction-history/components/date-picker'
import ButtonBlue from 'components/ui/button/_color/_blue'
import Popup from 'components/ui/popup'

import cn from 'classnames'

import styles from './styles.module.scss'
import { useTranslation } from 'utils/use-translation'

interface DateSelect {
  startDate: any
  endDate: any
  handleChangeCalendar: any
}

interface Sorting {
  label: string
  state: number
  setState: Dispatch<SetStateAction<number>>
  value: number
}

interface FilterType {
  types: string[]
  selectedTypes: string[]
  onSelect: (types: string | string[]) => void
}

interface FilterStatus {
  statuses: string[]
  selectedStatuses: string[]
  onSelect: (status: string | string[]) => void
}

interface AddFilterPopupProps {
  onClose: () => void
  dateSelect?: DateSelect
  sortings?: Sorting[]
  typeFilter?: FilterType
  statusFilter?: FilterStatus
}

export const AddFilterPopup: FC<AddFilterPopupProps> = ({
  onClose,
  dateSelect,
  sortings,
  typeFilter,
  statusFilter,
}) => {
  const { t } = useTranslation() 
  const initialSelectedTypes = typeFilter?.selectedTypes || []
  const initialSelectedStatuses = statusFilter?.selectedStatuses || []

  const [selectedTypes, setSelectedTypes] = useState<string[]>(initialSelectedTypes || [])
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(initialSelectedStatuses || [])

  const [selectedSortOption, setSelectedSortOption] = useState('')

  const handleTypeChange = (type: string) => {
    setSelectedTypes((prevSelectedTypes) => {
      if (prevSelectedTypes.includes(type)) {
        return prevSelectedTypes.filter((selectedType) => selectedType !== type)
      } else {
        return [...prevSelectedTypes, type]
      }
    })
  }

  const handleStatusChange = (status: string) => {
    setSelectedStatuses((prevSelectedStatuses) => {
      if (prevSelectedStatuses.includes(status)) {
        return prevSelectedStatuses.filter((selectedStatus) => selectedStatus !== status)
      } else {
        return [...prevSelectedStatuses, status]
      }
    })
  }

  const handleSortOptionChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setSelectedSortOption(value)
  }

  const clearAllFilters = () => {
    setSelectedTypes([])
    setSelectedStatuses([])
    setSelectedSortOption('')
    handleClearCalendar()
  }

  const applyFilters = () => {
    const selectedSorting = sortings?.find((sorting) => sorting.label === selectedSortOption)
    const otherSorting = sortings?.filter((sorting) => sorting.label !== selectedSortOption)

    otherSorting.forEach((item) => item.setState(0))

    if (selectedSorting) {
      selectedSorting.setState(selectedSorting.value)
    }

    const changedTypes = selectedTypes.filter((type) => !initialSelectedTypes.includes(type))
    initialSelectedTypes.forEach((initialType) => {
      if (!selectedTypes.includes(initialType)) {
        changedTypes.push(initialType)
      }
    })
    typeFilter.onSelect(changedTypes)

    const changedStatuses = selectedStatuses.filter((status) => !initialSelectedStatuses.includes(status))
    initialSelectedStatuses.forEach((initialStatus) => {
      if (!selectedStatuses.includes(initialStatus)) {
        changedStatuses.push(initialStatus)
      }
    })
    statusFilter.onSelect(changedStatuses)

    if (startDate && endDate) {
      dateSelect.handleChangeCalendar([startDate, endDate])
    }

    onClose()
  }

  useEffect(() => {
    const currentSorting = sortings?.find((sorting) => sorting.state === sorting.value)
    if (currentSorting) {
      const otherSortings = sortings?.filter((sorting) => sorting.state !== currentSorting.state)
      otherSortings?.forEach((sorting) => sorting.setState(0))
      setSelectedSortOption(currentSorting.label)
    }
    if (dateSelect.startDate && dateSelect.endDate) {
      handleChangeCalendar([dateSelect.startDate, dateSelect.endDate])
    }
  }, [])

  const [showCalendar, setShowCalendar] = useState(false)
  const [startDate, setStartDate] = useState(dateSelect.startDate)
  const [endDate, setEndDate] = useState(null)

  const handleChangeCalendar = (value) => {
    setStartDate(value[0])
    setEndDate(value[1])
    setShowCalendar(false)
  }

  const handleClearCalendar = () => {
    setEndDate(null)
    setStartDate(null)
  }

  return (
    <Popup onClose={onClose} heading={t('filter_option')} className={styles.wrapper}>
      <div className={styles.container}>
        <button
          className={cn(styles.clearAllButton, {
            [styles.hidden]:
              !startDate &&
              !endDate &&
              selectedSortOption.length === 0 &&
              selectedTypes.length === 0 &&
              selectedStatuses.length === 0,
          })}
          onClick={clearAllFilters}
        >{t('clear_all')}</button>
        {dateSelect && (
          <>
            <div className={styles.datePicker}>
              <DatePicker
                startDate={startDate}
                handleChangeCalendar={handleChangeCalendar}
                handleClearCalendar={handleClearCalendar}
                endDate={endDate}
                setShowCalendar={setShowCalendar}
                showCalendar={showCalendar}
              />
            </div>

            <div className={styles.margin} />
          </>
        )}

        {typeFilter?.types.length ? (
          <>
            <div className={styles.title}>{t('select_type')}</div>
            <div className={styles.sortList}>
              {typeFilter.types.map((cur) => (
                <label className={styles.item} key={cur}>
                  <input type="checkbox" checked={selectedTypes.includes(cur)} onChange={() => handleTypeChange(cur)} />
                  <p>{cur.toLowerCase() === t('monthly_interest') ? t('loan_interest') : cur.toLowerCase()}</p>
                </label>
              ))}
            </div>
            <hr className={styles.divider} />
          </>
        ) : (
          ''
        )}

        {statusFilter?.statuses.length ? (
          <>
            <div className={styles.title}>{t('select_status')}</div>
            <div className={styles.sortList}>
              {statusFilter.statuses.map((cur) => (
                <label className={styles.item} key={cur}>
                  <input
                    type="checkbox"
                    checked={selectedStatuses.includes(cur)}
                    onChange={() => handleStatusChange(cur)}
                  />
                  <p>{cur.toLowerCase()}</p>
                </label>
              ))}
            </div>
            <hr className={styles.divider} />
          </>
        ) : (
          ''
        )}

        <div className={styles.title}>{t('sort_by')}</div>
        <div className={styles.sortList}>
          {sortings?.map(({ label }) => (
            <label className={styles.item} key={label}>
              <input
                type="radio"
                name="sortOption"
                value={label}
                checked={selectedSortOption.includes(label)}
                onChange={handleSortOptionChange}
              />
              <p>{label}</p>
            </label>
          ))}
        </div>

        <ButtonBlue className={styles.apply_btn} onClick={applyFilters}>{t('apply_filter')}</ButtonBlue>
      </div>
    </Popup>
  )
}
